import { useCallback } from "react";
import { useQuery } from "@apollo/client";
import { GET_MY_ACCESS, GET_MY_ACCOUNTS } from "../api/graphql/team";
import { useAccountStore } from "../store/accountStore";

/**
 * Which account the person is working in, whether it is their own, and what
 * their role there lets them do.
 *
 * A teammate works inside the owner's account. What the owner alone may do —
 * the profile, the MCP keys — is hidden behind isOwnAccount; what a role
 * may do — deleting apps, placing orders, managing the team — behind can().
 * The backend refuses all of it anyway; this is what keeps the screens from
 * offering it.
 */

export type TeamRole = "ADMIN" | "MEMBER" | "VIEWER";

export type TeamPermission =
  | "apps:write"
  | "apps:delete"
  | "orders:create"
  | "orders:manage"
  | "team:manage";

export interface AccountOption {
  id: number;
  name: string | null;
  email: string | null;
  isOwn: boolean;
  /** The person's role there; null in their own account. */
  role: TeamRole | null;
}

export const ROLE_LABELS: Record<TeamRole, string> = {
  ADMIN: "Admin",
  MEMBER: "Member",
  VIEWER: "Viewer",
};

export const useAccountAccess = () => {
  const activeId = useAccountStore((state) => state.activeId);
  const { data, loading } = useQuery(GET_MY_ACCOUNTS, {
    fetchPolicy: "cache-first",
  });
  // Answered for the account in the header, so switching accounts — which
  // reloads the app — is what refreshes it.
  const { data: accessData } = useQuery(GET_MY_ACCESS, {
    fetchPolicy: "cache-first",
  });

  const accounts: AccountOption[] = data?.myAccounts ?? [];
  const ownAccount = accounts.find((account) => account.isOwn) ?? null;
  const activeAccount =
    accounts.find((account) => account.id === activeId) ?? ownAccount;

  const access = accessData?.myAccess;
  const permissions: string[] | null = access?.permissions ?? null;

  /**
   * Whether the role allows this. Until the answer arrives everything is
   * allowed: the own account is the common case, and a button that appears
   * and is then refused is better than one that flickers away.
   */
  const can = useCallback(
    (permission: TeamPermission) =>
      permissions ? permissions.includes(permission) : true,
    [permissions],
  );

  return {
    loading,
    accounts,
    ownAccount,
    activeAccount,
    // While the list is loading nothing is hidden yet, for the reason above.
    isOwnAccount: activeAccount ? activeAccount.isOwn : true,
    /** More than one account means the switcher is worth showing at all. */
    hasOtherAccounts: accounts.length > 1,
    role: (access?.role ?? null) as TeamRole | null,
    /** Applications.id the person is limited to; null is every app. */
    applicationIds: (access?.applicationIds ?? null) as number[] | null,
    can,
  };
};
